//
// This started from a verbatim copy of the Auth0 SPA - angular quickstart:
// https://manage.auth0.com/dashboard/eu/dev-84dnqf5m/applications/J86Nk1jYacCtfJxEIdhWAgUk4O6fmwkJ/quickstart
//
// customization:
// - use configurationService to dynamically inject auth0 config
// - extend auth info by extracting data from the provided token (tokenToUserInfo)
//
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import createAuth0Client, { GetTokenSilentlyOptions, RedirectLoginResult } from '@auth0/auth0-spa-js';
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { ConfigurationService } from '../config/configuration.service';
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    // Create an observable of Auth0 instance of client
    private auth0Client$: Observable<Auth0Client>;
    private auth0ClientId: string;

    userInfo$: Observable<UserInfo>;

    handleRedirectCallback$: Observable<RedirectLoginResult>;

    // Create subject and public observable of user profile data
    private userProfileSubject$ = new BehaviorSubject<any>(null);
    userProfile$ = this.userProfileSubject$.asObservable();

    private claimsSubject$ = new BehaviorSubject<any>(null);
    claims$ = this.claimsSubject$.asObservable();

    private authCallbackSubject$ = new BehaviorSubject<boolean>(false);
    authCallback$ = this.authCallbackSubject$.asObservable();

    constructor(private router: Router, configurationService: ConfigurationService) {
        this.auth0ClientId = configurationService.auth0ClientId;
        this.auth0Client$ = (from(
            createAuth0Client({
                domain: configurationService.auth0Domain,
                client_id: configurationService.auth0ClientId,
                audience: configurationService.auth0Audience,
                redirect_uri: `${window.location.origin}`,
                scope: 'openid profile email',
            })
        ) as Observable<Auth0Client>).pipe(
            shareReplay(1), // Every subscription receives the same shared value
            catchError((err) => throwError(err))
        );

        this.userInfo$ = this.authCallback$.pipe(
            filter((isCallbackProcessed) => isCallbackProcessed === true),
            concatMap(() => this.auth0Client$),
            concatMap((auth0Client: Auth0Client) => from(auth0Client.isAuthenticated())),
            mergeMap((isAuthenticated) => {
                if (isAuthenticated) {
                    return this.getTokenSilently$().pipe(map((token) => AuthService.tokenToUserInfo(token)));
                }
                return of(<UserInfo>{ isLoggedIn: false });
            }),
            shareReplay(1), // Every subscription receives the same shared value
            catchError((err) => throwError(err))
        );

        this.handleRedirectCallback$ = this.auth0Client$.pipe(concatMap((client: Auth0Client) => from(client.handleRedirectCallback())));
        // On initial load, check authentication state with authorization server
        // Set up local auth streams if user is already authenticated
        this.localAuthSetup();
        // Handle redirect from Auth0 login
        this.handleAuthCallback();
    }

    // When calling, options can be passed if desired
    // https://auth0.github.io/auth0-spa-js/classes/auth0client.html#getuser
    getUser$(options?): Observable<any> {
        return this.auth0Client$.pipe(
            concatMap((client: Auth0Client) => from(client.getUser(options))),
            tap((user) => this.userProfileSubject$.next(user))
        );
    }

    getTokenSilently$(options?: GetTokenSilentlyOptions): Observable<string> {
        return this.auth0Client$.pipe(concatMap((client: Auth0Client) => from(client.getTokenSilently(options))));
    }

    private localAuthSetup() {
        // This should only be called on app initialization
        // Set up local authentication streams
        const checkAuth$ = this.userInfo$.pipe(
            concatMap((userInfo) => {
                if (userInfo.isLoggedIn) {
                    // If authenticated, get user and set in app
                    // NOTE: you could pass options here if needed
                    return this.getUser$();
                }
                // If not authenticated, return stream that emits 'false'
                return of(false);
            })
        );
        checkAuth$.subscribe();
    }

    login(redirectPath: string = '/') {
        // A desired redirect path can be passed to login method
        // (e.g., from a route guard)
        // Ensure Auth0 client instance exists
        this.auth0Client$.subscribe((client: Auth0Client) => {
            // Call method to log in
            client.loginWithRedirect({
                redirect_uri: `${window.location.origin}`,
                appState: { target: redirectPath },
            });
        });
    }

    private handleAuthCallback() {
        // Call when app reloads after user logs in with Auth0
        const params = window.location.search;
        if (params.includes('code=') && params.includes('state=')) {
            let targetRoute: string; // Path to redirect to after login processsed
            const authComplete$ = this.handleRedirectCallback$.pipe(
                tap((cbRes) => {
                    // Get and set target redirect route from callback results
                    targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/';
                }),
                concatMap(() => this.getUser$())
            );
            authComplete$.subscribe((user) => {
                // Redirect to target route after callback processing
                this.router.navigate([targetRoute]);
                this.authCallbackSubject$.next(true);
            });
        } else {
            this.authCallbackSubject$.next(true);
        }
    }

    logout() {
        // Ensure Auth0 client instance exists
        this.auth0Client$.subscribe((client: Auth0Client) => {
            // Call method to log out
            client.logout({
                client_id: this.auth0ClientId,
                returnTo: `${window.location.origin}`,
            });
        });
    }

    private static tokenToUserInfo(token: string): UserInfo {
        if (!token) {
            return <UserInfo>{
                isLoggedIn: false,
            };
        }
        const decodedToken = jwt_decode(token);
        const permissions: string[] = decodedToken['permissions'] || [];
        const practiceId: string = decodedToken['https://schemas.addinternational.nl/ws/2020/claims/practiceId'];
        const groupId: string = decodedToken['https://schemas.addinternational.nl/ws/2020/claims/groupId'];
        const isAdmin = permissions.indexOf('admin') !== -1;
        const isPracticeManager = permissions.indexOf('practiceManager') !== -1;
        const isGroupManager = permissions.indexOf('groupManager') !== -1;
        return <UserInfo>{
            isLoggedIn: true,
            practiceId: practiceId,
            groupId: groupId,
            isAdmin: isAdmin,
            isPracticeManager: isPracticeManager,
            isGroupManager: isGroupManager,
        };
    }
}

export interface UserInfo {
    isLoggedIn: boolean;
    isAdmin: boolean;
    isPracticeManager: boolean;
    isGroupManager: boolean;
    practiceId: string;
    groupId: string;
}
